import React, {useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {CustomModal} from '@/app/modules/widgets/components/CustomModal'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {submitQuoteRequest} from './api'
import {
  CatalogModule,
  MAX_EXTRA_SEATS,
  RENEW_YEARS,
  RenewYears,
  missingCatalogModules,
} from './catalog'
import {ModuleCheckboxList} from './ModuleCheckboxList'
import {QuantityStepper} from './QuantityStepper'

export type QuotePurchaseKind = 'add_module' | 'add_seat' | 'renew'

const TITLES: Record<QuotePurchaseKind, string> = {
  add_module: 'Add modules',
  add_seat: 'Add seats',
  renew: 'Renew OmicsBox Subscription',
}

const TITLE_ICONS: Record<QuotePurchaseKind, string> = {
  add_module: 'fas fa-puzzle-piece',
  add_seat: 'fas fa-user-plus',
  renew: 'fas fa-sync-alt',
}

interface QuoteRequestDialogProps {
  kind: QuotePurchaseKind
  subscriptionId: string | null
  /** Modules already on the subscription (from subscription details). */
  ownedModules?: string[] | null
  /** Modules ticked when the dialog opens (e.g. ENGINE from the AI chat paywall); owned ones are dropped. */
  initialModules?: CatalogModule[]
  closeModal: () => void
}

export const QuoteRequestDialog: React.FC<QuoteRequestDialogProps> = ({
  kind,
  subscriptionId,
  ownedModules,
  initialModules,
  closeModal,
}) => {
  const availableModules = useMemo(() => missingCatalogModules(ownedModules), [ownedModules])

  const [selectedModules, setSelectedModules] = useState<CatalogModule[]>(() =>
    (initialModules ?? []).filter((m) => availableModules.includes(m))
  )
  const [seatCount, setSeatCount] = useState(1)
  const [requestExtraSeats, setRequestExtraSeats] = useState(false)
  const [requestExtraModules, setRequestExtraModules] = useState(false)
  const [requestRenewalPeriod, setRequestRenewalPeriod] = useState(false)
  const [renewYears, setRenewYears] = useState<RenewYears | ''>('')
  const [comments, setComments] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const canSubmit = (() => {
    if (isSubmitting) return false
    if (kind === 'add_module') return selectedModules.length > 0
    if (kind === 'add_seat') return seatCount >= 1
    if (kind === 'renew') {
      if (requestExtraModules && selectedModules.length === 0) return false
      if (requestRenewalPeriod && renewYears === '') return false
      return true
    }
    return false
  })()

  const handleRequestQuote = async () => {
    if (!canSubmit || isSubmitting) return
    setIsSubmitting(true)
    try {
      if (kind === 'add_module') {
        await submitQuoteRequest({kind, modules: selectedModules})
      } else if (kind === 'add_seat') {
        await submitQuoteRequest({kind, seats: seatCount})
      } else {
        await submitQuoteRequest({
          kind,
          request_renewal_period: requestRenewalPeriod,
          renew_years: requestRenewalPeriod && renewYears !== '' ? renewYears : undefined,
          request_extra_seats: requestExtraSeats,
          request_extra_modules: requestExtraModules,
          seats: requestExtraSeats ? seatCount : undefined,
          modules: requestExtraModules ? selectedModules : undefined,
          comments: comments.trim() || undefined,
        })
      }
      setSubmitted(true)
    } catch (err: unknown) {
      const message =
        (err as {response?: {data?: {message?: string}}; message?: string})?.response?.data
          ?.message ||
        (err as {message?: string})?.message ||
        'Could not send the quote request'
      toast.error(message, toastDefaultConfig)
      getLogger().error('Quote request failed:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <CustomModal
        title='Request received'
        titleIcon='fas fa-check-circle'
        closeModal={closeModal}
        buttons={[
          {
            text: 'Close',
            className: 'btn btn-primary',
            onClick: closeModal,
          },
        ]}
      >
        <p className='mb-0'>
          Thank you. Your request has been sent. Our sales team will follow up with a quote,
          including online payment and purchase-order options.
        </p>
      </CustomModal>
    )
  }

  return (
    <CustomModal
      title={TITLES[kind]}
      titleIcon={TITLE_ICONS[kind]}
      closeModal={closeModal}
      dialogClassName={kind === 'renew' ? 'modal-lg' : undefined}
      buttons={[
        {
          text: 'Cancel',
          className: 'btn btn-light',
          onClick: closeModal,
          disabled: isSubmitting,
        },
        {
          text: isSubmitting ? 'Sending…' : 'Request Quote',
          className: 'btn btn-primary',
          onClick: () => void handleRequestQuote(),
          disabled: !canSubmit,
        },
      ]}
    >
      <div>
        {subscriptionId ? (
          <p className='text-muted small mb-3'>
            Subscription reference: <strong>{subscriptionId}</strong>
          </p>
        ) : null}

        {kind === 'add_module' && (
          <>
            <p className='mb-3'>
              Select the modules you would like to add. Card payment is not available for this yet —
              request a quote and we will send pricing for your selection.
            </p>
            <ModuleCheckboxList
              modules={availableModules}
              selected={selectedModules}
              disabled={isSubmitting}
              onChange={setSelectedModules}
            />
          </>
        )}

        {kind === 'add_seat' && (
          <>
            <p className='mb-3'>
              Choose how many additional seats you need (1–{MAX_EXTRA_SEATS}). Card payment is not
              available for this yet — request a quote and we will send pricing.
            </p>
            <QuantityStepper
              label='Seats'
              value={seatCount}
              min={1}
              max={MAX_EXTRA_SEATS}
              disabled={isSubmitting}
              onChange={setSeatCount}
              labelledById='add-seat-quantity-label'
            />
          </>
        )}

        {kind === 'renew' && (
          <>
            <p className='mb-3'>
              Adjust the options below if you want to modify your subscription. Request a quote and
              our sales team will follow up.
            </p>

            <div className='form-check mb-2'>
              <input
                className='form-check-input'
                type='checkbox'
                id='renew-period'
                checked={requestRenewalPeriod}
                disabled={isSubmitting}
                onChange={(e) => {
                  setRequestRenewalPeriod(e.target.checked)
                  if (!e.target.checked) setRenewYears('')
                }}
              />
              <label className='form-check-label' htmlFor='renew-period'>
                Request a change of renewal period
              </label>
            </div>
            {requestRenewalPeriod && (
              <div className='mb-3 ms-4' style={{maxWidth: '12rem'}}>
                <label className='form-label' htmlFor='renew-years-select'>
                  Period
                </label>
                <select
                  id='renew-years-select'
                  className='form-select'
                  value={renewYears === '' ? '' : String(renewYears)}
                  disabled={isSubmitting}
                  onChange={(e) => {
                    const value = e.target.value
                    setRenewYears(value === '' ? '' : (Number(value) as RenewYears))
                  }}
                >
                  <option value='' disabled>
                    Select years…
                  </option>
                  {RENEW_YEARS.map((years) => (
                    <option key={years} value={years}>
                      {years} {years === 1 ? 'year' : 'years'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className='form-check mb-2'>
              <input
                className='form-check-input'
                type='checkbox'
                id='renew-extra-seats'
                checked={requestExtraSeats}
                disabled={isSubmitting}
                onChange={(e) => setRequestExtraSeats(e.target.checked)}
              />
              <label className='form-check-label' htmlFor='renew-extra-seats'>
                Request additional seats
              </label>
            </div>
            {requestExtraSeats && (
              <div className='mb-3 ms-4'>
                <QuantityStepper
                  label='Seats'
                  value={seatCount}
                  min={1}
                  max={MAX_EXTRA_SEATS}
                  disabled={isSubmitting}
                  onChange={setSeatCount}
                  labelledById='renew-seat-quantity-label'
                />
              </div>
            )}

            {availableModules.length > 0 && (
              <>
                <div className='form-check mb-2'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='renew-extra-modules'
                    checked={requestExtraModules}
                    disabled={isSubmitting}
                    onChange={(e) => setRequestExtraModules(e.target.checked)}
                  />
                  <label className='form-check-label' htmlFor='renew-extra-modules'>
                    Request additional module(s)
                  </label>
                </div>
                {requestExtraModules && (
                  <div className='mb-3 ms-4'>
                    <ModuleCheckboxList
                      modules={availableModules}
                      selected={selectedModules}
                      disabled={isSubmitting}
                      onChange={setSelectedModules}
                    />
                  </div>
                )}
              </>
            )}

            <div className='mt-4 mb-0'>
              <label className='form-label' htmlFor='renew-comments'>
                Comments
              </label>
              <textarea
                id='renew-comments'
                className='form-control'
                rows={3}
                value={comments}
                disabled={isSubmitting}
                onChange={(e) => setComments(e.target.value)}
                placeholder='Leave a note for our sales team with anything that might help with this request'
              />
            </div>
          </>
        )}
      </div>
    </CustomModal>
  )
}
